import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import { AppTopbar } from './AppTopbar';
import { AppFooter } from './AppFooter';
import { AppMenu } from './AppMenu';
import { AppProfile } from './AppProfile';
import { Toast } from 'primereact/toast';

import Login from './pages/login/login';

import Router from './config/Router';
import { useSelector } from 'react-redux';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'prismjs/themes/prism-coy.css';
import '@fullcalendar/core/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import './assets/css/layout.scss';
import './App.scss';
import api from './config/Axios.js';
import Loader from './components/loader/Loader';
import logo from './assets/img/seplagmini.png';
import Menu from './config/Menu';
import style from './App.module.scss';
import { Link } from 'react-router-dom';
import { printToast } from './core/utils/Global';

const App = () => {
    const user = useSelector((state) => state.userReducer.user);

    window.$http = api();
    window.$toast = useRef(null);

    const [layoutMode] = useState('static');
    const [layoutColorMode] = useState('dark');
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [versaoAPI, setVersaoAPI] = useState('');
    const [inputStyle] = useState('outlined');
    const [ripple] = useState(false);
    const sidebar = useRef();
    let menuClick = false;

    useEffect(() => {
        window.$http
            .get('publicos/sistemas/versao')
            .then((res) => {
                setVersaoAPI(res.data);
                window.console.log(process.env.REACT_APP_BUILD_VERSION);
            })
            .catch((error) => {
                if (!error?.response) {
                    printToast({
                        severity: 'error',
                        summary: error.name,
                        detail: error.message,
                    });
                }
            });
    }, []);

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, 'body-overflow-hidden');
        } else {
            removeClass(document.body, 'body-overflow-hidden');
        }
    }, [mobileMenuActive]);

    const onWrapperClick = () => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
        menuClick = false;
    };

    const onToggleMenu = (event) => {
        menuClick = true;

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                setOverlayMenuActive((prevState) => !prevState);
            } else if (layoutMode === 'static') {
                setStaticMenuInactive((prevState) => !prevState);
            }
        } else {
            setMobileMenuActive((prevState) => !prevState);
        }
        event.preventDefault();
    };

    const onSidebarClick = () => {
        menuClick = true;
    };

    const onMenuItemClick = (event) => {
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    };

    const menu = Menu();

    const addClass = (element, className) => {
        if (element.classList) element.classList.add(className);
        else element.className += ` ${className}`;
    };

    const removeClass = (element, className) => {
        if (element.classList) {
            element.classList.remove(className);
        } else {
            element.className = element.className.replace(
                new RegExp(`(^|\\b)${className.split(' ').join('|')}(\\b|$)`, 'gi'),
                ' '
            );
        }
    };

    const isDesktop = () => {
        const teste =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
                navigator.userAgent
            )
                ? false
                : true;
        if (
            teste &&
            navigator.userAgent.includes('Macintosh') &&
            !navigator.userAgent.includes('Chrome')
        ) {
            return false;
        }
        return teste;
    };

    const isSidebarVisible = () => {
        if (isDesktop()) {
            if (layoutMode === 'static') return !staticMenuInactive;
            else if (layoutMode === 'overlay') return overlayMenuActive;
            else return true;
        }

        return mobileMenuActive;
    };

    const verificaUserLogado = () => {
        if (user) {
            return (
                <>
                    <AppTopbar onToggleMenu={onToggleMenu} isSidebarVisible={isSidebarVisible()} />
                    <CSSTransition
                        classNames={{
                            enter: style.enter,
                            enterActive: style.enterActive,
                            exit: style.exit,
                            exitActive: style.exitActive,
                        }}
                        className="layout-sidebar layout-sidebar-light"
                        timeout={{ enter: 200, exit: 500 }}
                        in={isSidebarVisible()}
                        unmountOnExit
                    >
                        <div ref={sidebar} className={sidebarClassName} onClick={onSidebarClick}>
                            <div className="layout-logo">
                                <Link to="/">
                                    <img alt="Logo" src={logo} style={{ cursor: 'pointer' }} />
                                </Link>
                            </div>
                            <AppProfile />
                            <AppMenu model={menu} onMenuItemClick={onMenuItemClick} />
                            <div
                                style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    top: '20px',
                                    color: 'rgb(255, 255, 255)',
                                    flex: '1',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                }}
                            >
                                Versão 0.0.2-o - {versaoAPI}
                            </div>
                        </div>
                    </CSSTransition>
                    <div className="layout-main">
                        <Router />
                    </div>
                    <AppFooter />
                </>
            );
        } else {
            return <Login />;
        }
    };

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
    });

    const sidebarClassName = classNames('layout-sidebar', {
        'layout-sidebar-dark': layoutColorMode === 'dark',
        'layout-sidebar-light': layoutColorMode === 'light',
    });

    return (
        <div className={wrapperClass} onClick={onWrapperClick}>
            <Toast ref={window.$toast} />
            <Loader />
            {verificaUserLogado()}
        </div>
    );
};

export default App;
